// Shared utilities for VoiceEnroll and VoiceEnrollStaff pages

import { EMOTION_KEYS } from './emotionConstants';

const API_ROOT = (() => {
  const env = process.env.REACT_APP_API_URL;
  if (env) return env.endsWith('/api') ? env.slice(0, -4) : env;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:8000';
  }
  return 'https://app.neurolonic.com/api';
})();

const PREDICT_ENDPOINT = (() => {
  const env = process.env.REACT_APP_PREDICT_URL;
  if (env) return `${env}/predict`;
  return `${API_ROOT}/app/predict`;
})();

function mapApiResponseToMetrics(apiResult) {
  const res = (apiResult && (apiResult.RES || apiResult.res || apiResult.data || apiResult)) || {};
  const top5String = res.top_5 || '';
  const scores = {};
  EMOTION_KEYS.forEach(em => {
    try {
      const re = new RegExp(`${em}\\s+(\\d+(?:\\.\\d+)?)%`, 'i');
      const m = top5String.match(re);
      scores[em] = m ? parseFloat(m[1]) / 100 : 0;
    } catch { scores[em] = 0; }
  });
  const calm = scores.happy ?? 0;
  const happy = scores.happy ?? 0;
  const stressed = scores.angry ?? 0;
  const demotivated = scores.sad ?? 0;
  const anxious = scores.fear ?? 0;
  const hurt = scores.disgust ?? 0;
  const burnout = Math.round(((scores.angry || 0) + (scores.sad || 0) + (scores.fear || 0)) * 100) || 0;
  return { calm, happy, stressed, demotivated, anxious, hurt, burnout };
}

export async function callPredictAPI(audioBlob) {
  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.wav');
  const res = await fetch(PREDICT_ENDPOINT, { method: 'POST', body: formData });
  if (!res.ok) throw new Error(`Predict failed ${res.status}`);
  const json = await res.json();
  return mapApiResponseToMetrics(json);
}

export function createSaveRecording({ endpointSuffix, token, setSaved, setError, setSaving }) {
  return async function saveRecording(recordingType, results) {
    setSaving(true);
    try {
      let emotionData;
      if (recordingType === '40s') {
        emotionData = results[0];
      } else {
        const keys = ['calm', 'stressed', 'demotivated', 'anxious', 'hurt'];
        const avg = keys.reduce((acc, k) => {
          acc[k] = results.reduce((s, r) => s + (Number(r[k] || 0)), 0) / results.length;
          return acc;
        }, {});
        const burnRaw = results.reduce((s, r) => s + Number(r.burnout || 0), 0) / results.length;
        const burn = Number.isFinite(burnRaw) ? Math.round(burnRaw) : 0;
        emotionData = { ...avg, burnout: burn };
      }
      const res = await fetch(`${API_ROOT}/${endpointSuffix}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          recording_type: recordingType,
          emotion_data: emotionData
        })
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      const body = await res.json();
      if (body.status_code === 200) {
        setSaved(true);
      } else {
        setError(body.message || 'Failed to save');
      }
    } catch (e) {
      setError('Failed to save recording');
    }
    setSaving(false);
  };
}

export function createSubmitForm({ endpointSuffix, token, formData, setShowForm, setError, setFormSaving }) {
  return async function submitForm() {
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    setFormSaving(true);
    try {
      const res = await fetch(`${API_ROOT}/${endpointSuffix}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          recording_type: 'form',
          emotion_data: formData
        })
      });
      if (!res.ok) throw new Error(`Submit failed: ${res.status}`);
      const body = await res.json();
      if (body.status_code === 200) {
        setShowForm(false);
      } else {
        setError(body.message || 'Failed to save form');
      }
    } catch (e) {
      setError('Failed to save your information');
    }
    setFormSaving(false);
  };
}

export function createLoad({ endpointSuffix, token, setData, setSaved, setShowForm, setFormData, setError, setLoading, logPrefix }) {
  return async function load() {
    if (!token) {
      setError('Missing token. Please use the link from your email.');
      setLoading(false);
      return;
    }
    try {
      const url = `${API_ROOT}/${endpointSuffix}?token=${token}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Load failed: ${response.status}`);
      const res = await response.json();
      if (res.status_code !== 200) {
        setError(res.message || 'Invalid or expired token');
        setLoading(false);
        return;
      }
      setData(res.data);
      if (res.data.voice_recorded) {
        setSaved(true);
        setShowForm(false);
      }
      if (res.data.name) {
        setFormData(prev => ({ ...prev, name: res.data.name }));
      }
    } catch (e) {
      console.error(`[${logPrefix}] Load error:`, e);
      setError('Failed to load. Please check your link.');
    }
    setLoading(false);
  };
}

function createStartRecording(deps, options = {}) {
  const { continuous = false, segmentDuration = 8000, totalDuration = 40000 } = options;
  const { saveRecording, callPredictAPI, resultsRef, setAllResults, setSegmentCount, setCurrentSegment, setRecording, setTimer, streamRef, mediaRecorderRef, audioChunksRef, intervalRef, autoStopRef, setError } = deps;
  const duration = continuous ? totalDuration : segmentDuration;

  return async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];

      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size) audioChunksRef.current.push(ev.data);
      };

      mr.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        try {
          const metrics = await callPredictAPI(blob);
          if (continuous) {
            setAllResults([metrics]);
            await saveRecording('40s', [metrics]);
          } else {
            const newResults = [...resultsRef.current, metrics];
            resultsRef.current = newResults;
            setAllResults(newResults);
            setSegmentCount(newResults.length);

            if (newResults.length >= 5) {
              await saveRecording('8s', newResults);
            } else {
              setCurrentSegment(newResults.length);
              setRecording(false);
              if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
              }
            }
          }
        } catch (err) {
          console.error('Recording prediction error:', err);
          setError('Failed to process recording. Please try again.');
          setRecording(false);
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
          }
        }
      };

      mr.start();
      setRecording(true);
      setTimer(duration / 1000);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => setTimer(t => Math.max(t - 1, 0)), 1000);
      if (autoStopRef.current) clearTimeout(autoStopRef.current);
      autoStopRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, duration);
    } catch (e) {
      console.error('Microphone access error:', e);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (autoStopRef.current) {
        clearTimeout(autoStopRef.current);
        autoStopRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      setRecording(false);
      setTimer(40);
      setError('Microphone access denied. Please allow microphone access and try again.');
    }
  };
}

export const createStartSegmentRecording = (deps) => createStartRecording(deps, { continuous: false });
export const createStartContinuousRecording = (deps) => createStartRecording(deps, { continuous: true });
